import { useEffect, useRef } from 'react';
import { IconFilter, IconArrowDown, IconInfo } from '@components/icons';
import { MenuSelect, type MenuSelectItem } from '@components/MenuSelect';
import styles from './SelectFilter.module.scss';

export type SelectFilterSize = 'regular' | 'large';

export interface SelectFilterProps {
  /** Label displayed in the trigger button */
  label: string;
  /** Array of selectable items */
  items: MenuSelectItem[];
  /** Currently selected value (single-select mode) */
  value?: string | null;
  /** Callback when selection changes (single-select mode) */
  onChange?: (value: string) => void;
  /** Enable multi-select mode */
  multiSelect?: boolean;
  /** Array of selected values (multi-select mode) */
  selectedValues?: string[];
  /** Callback when selection changes (multi-select mode) */
  onSelectionChange?: (values: string[]) => void;
  /** Whether the dropdown is open (controlled) */
  isOpen: boolean;
  /** Callback when open state changes */
  onOpenChange: (isOpen: boolean) => void;
  /** Size variant - 'regular' (38px) or 'large' (52px) */
  size?: SelectFilterSize;
  /** Maximum height for dropdown before scrolling (default: 240px) */
  maxHeight?: number;
  /** Enable type-to-filter input inside the dropdown */
  filterable?: boolean;
  /** Show AND-logic notice at the bottom of the dropdown when 2+ items are selected */
  showAndNotice?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export const SelectFilter = ({
  label,
  items,
  value,
  onChange,
  multiSelect = false,
  selectedValues,
  onSelectionChange,
  isOpen,
  onOpenChange,
  size = 'regular',
  maxHeight = 240,
  filterable = false,
  showAndNotice = false,
  className = '',
}: SelectFilterProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Count selected items for badge
  const selectedCount = multiSelect ? (selectedValues?.length ?? 0) : (value ? 1 : 0);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onOpenChange]);

  // Handle escape key to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onOpenChange]);

  const handleTriggerClick = () => {
    onOpenChange(!isOpen);
  };

  const handleSelect = (selectedValue: string) => {
    if (multiSelect) {
      // Multi-select: handled by MenuSelect's onSelectionChange; keep dropdown open
      return;
    }
    onChange?.(selectedValue);
    onOpenChange(false);
  };

  const containerClassName = [styles.container, className].filter(Boolean).join(' ');
  const triggerClassName = [
    styles.trigger,
    styles[size],
    isOpen && styles.open,
  ].filter(Boolean).join(' ');
  const dropdownClassName = [styles.dropdown, styles[`dropdown${size.charAt(0).toUpperCase() + size.slice(1)}`]].join(' ');

  return (
    <div className={containerClassName} ref={containerRef}>
      <button
        type="button"
        className={triggerClassName}
        onClick={handleTriggerClick}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <IconFilter size={16} className={styles.filterIcon} />
        <span className={styles.label}>{label}</span>
        <IconArrowDown size={16} className={styles.arrowIcon} />
        {selectedCount > 0 && (
          <span className={styles.badge}>{selectedCount}</span>
        )}
      </button>

      {isOpen && (
        <div className={dropdownClassName}>
          <MenuSelect
            items={items}
            value={multiSelect ? undefined : value}
            onChange={multiSelect ? undefined : handleSelect}
            multiSelect={multiSelect}
            selectedValues={multiSelect ? selectedValues : undefined}
            onSelectionChange={multiSelect ? onSelectionChange : undefined}
            maxHeight={maxHeight}
            filterable={filterable}
          />
          {showAndNotice && (selectedValues?.length ?? 0) >= 2 && (
            <div className={styles.andNotice}>
              <IconInfo size={12} className={styles.andNoticeIcon} />
              <span>Multiple statuses use AND logic</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
